import React, { useState, useMemo } from "react";
import { TREATMENTS, FILTERS, type TreatmentData } from "./treatmentData";

/**
 * Interactive treatment comparison tool.
 * Visitors tick 2–3 treatments and see them side by side.
 * Filter chips narrow the selection. The tool does NOT recommend
 * or score — it only displays the same cited figures as the table.
 */
const CompareTool: React.FC = () => {
  const [selected, setSelected] = useState<string[]>(["saint", "standard-tms"]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((s) => s !== id)
        : prev.length < 3
          ? [...prev, id]
          : prev,
    );
  };

  const visible = useMemo(() => {
    if (!activeFilter) return TREATMENTS;
    return TREATMENTS.filter(
      (t) => t[activeFilter as keyof TreatmentData] === true,
    );
  }, [activeFilter]);

  const compared = TREATMENTS.filter((t) => selected.includes(t.id));

  return (
    <div className="not-prose">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="text-sm font-semibold text-primary self-center mr-1">
          Filter:
        </span>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() =>
              setActiveFilter((prev) => (prev === f.key ? null : f.key))
            }
            className={`rounded-full px-4 py-2 text-xs sm:text-sm font-semibold border transition-colors ${
              activeFilter === f.key
                ? "bg-accent text-accent-foreground border-accent"
                : "bg-card text-foreground/80 border-border hover:border-accent/50"
            }`}
          >
            {f.label}
          </button>
        ))}
        {activeFilter && (
          <button
            type="button"
            onClick={() => setActiveFilter(null)}
            className="rounded-full px-3 py-2 text-xs font-medium text-muted-foreground hover:text-primary"
          >
            Clear ✕
          </button>
        )}
      </div>

      {/* Treatment selector */}
      <div className="mb-5">
        <p className="text-sm text-foreground/80 mb-3">
          Tick 2–3 treatments to compare them side by side:
        </p>
        <div className="flex flex-wrap gap-2">
          {visible.map((t) => {
            const isSelected = selected.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className={`rounded-lg px-4 py-2.5 text-sm font-semibold border-2 transition-all ${
                  isSelected
                    ? "bg-primary text-white border-primary shadow-soft"
                    : "bg-card text-foreground/80 border-border hover:border-primary/40"
                }`}
              >
                {isSelected && <span className="mr-1.5">✓</span>}
                {t.name}
              </button>
            );
          })}
        </div>
        {selected.length >= 3 && (
          <p className="text-xs text-muted-foreground mt-2">
            Maximum of 3 selected. Deselect one to add another.
          </p>
        )}
      </div>

      {/* Comparison cards */}
      {compared.length < 2 ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Select at least two treatments to see them compared side by side.
          </p>
        </div>
      ) : (
        <div
          className={`grid gap-4 ${
            compared.length === 2
              ? "sm:grid-cols-2"
              : "sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {compared.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden flex flex-col"
            >
              <div
                className={`px-4 py-3 ${t.id === "saint" ? "bg-accent" : "bg-primary"}`}
              >
                <h3
                  className={`font-headings text-base font-bold ${t.id === "saint" ? "text-accent-foreground" : "text-white"}`}
                >
                  {t.name}
                </h3>
              </div>
              <dl className="divide-y divide-border flex-1">
                <div className="px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    FDA status
                  </dt>
                  <dd className="text-sm text-foreground/90 mt-1">
                    {t.fdaStatus}
                  </dd>
                </div>
                <div className="px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Time commitment
                  </dt>
                  <dd className="text-sm text-foreground/90 mt-1">
                    {t.timeCommitment}
                  </dd>
                </div>
                <div className="px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Time to response
                  </dt>
                  <dd className="text-sm text-foreground/90 mt-1">
                    {t.timeToResponse}
                  </dd>
                </div>
                <div className="px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Response / remission
                  </dt>
                  <dd className="text-sm text-foreground/90 mt-1">
                    {t.responseRemission}
                  </dd>
                </div>
                <div className="px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Common side effects
                  </dt>
                  <dd className="text-sm text-foreground/90 mt-1">
                    {t.sideEffects}
                  </dd>
                </div>
                <div className="px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Cost / coverage
                  </dt>
                  <dd className="text-sm text-foreground/90 mt-1">
                    {t.costCoverage}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground italic mt-4">
        This tool is a reference, not a recommendation. It does not score or
        rank treatments. The right choice depends on your diagnosis, history,
        and circumstances and is made at a consultation. Individual results may
        vary.
      </p>

      <div className="mt-5 text-center">
        <a
          href="/am-i-a-candidate-screener"
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-accent text-accent-foreground font-semibold px-6 py-3 text-sm shadow-soft hover:opacity-90 transition-opacity"
        >
          Learn If It's a Fit →
        </a>
      </div>
    </div>
  );
};

export default CompareTool;
